import type { ApiDashboardElement, SourceMap } from '@highimpact-frontend/api-types';
import {
  DEFAULT_DECIMATION_POLICY,
  useSessionCacheTransport,
  type DecimationPolicy,
} from '@highimpact/react-hooks';
import type { ThresholdRequestContextPrefs } from '@highimpact/scatterplot';
import { useMemo } from 'react';
import {
  buildMeasureRequestFromElement,
  clonePythonOnlyMeasureBody,
  createEmptyMeasureBody,
  MEASURES_V2_URL,
  type ElementMeasureRequest,
  type MeasureRequestContext,
} from '../../../../runtime/buildMeasureRequestFromElement';
import type { ElementMeasureKind } from '../../../../runtime/elementMeasureSupport';
import { getElementMeasureKind, supportsElementMeasureRequest } from '../../../../runtime/elementMeasureSupport';
import type { DashboardScope } from '../../../../state/dashboardRuntimeTypes';

import { useDashboardScope } from '../../../../context/DashboardHostContext';
import type { MeasureRow } from '../../../../runtime/measureRow.types';

export type ElementMeasurePlotType =
  | 'timeseries'
  | 'scatter'
  | 'windrose'
  | 'bar'
  | 'ombrothermic';

export type { ElementMeasureKind };

export interface UseElementMeasureTransportOptions {
  readonly element: ApiDashboardElement;
  readonly plotType?: ElementMeasurePlotType;
  readonly thresholdRequest?: ThresholdRequestContextPrefs;
  readonly scope?: DashboardScope;
  readonly decimationPolicy?: DecimationPolicy;
  readonly sourceLabelMap?: SourceMap;
}

function resolveMeasureKindFromElement(
  element: ApiDashboardElement,
  plotTypeOverride?: ElementMeasurePlotType
): ElementMeasureKind | undefined {
  if (plotTypeOverride) {
    return plotTypeOverride;
  }

  return getElementMeasureKind(element);
}

/**
 * Session-cache measure transport derived from dashboard element JSON.
 */
export function useElementMeasureTransport({
  element,
  plotType: plotTypeOverride,
  thresholdRequest,
  scope: scopeOverride,
  decimationPolicy = DEFAULT_DECIMATION_POLICY,
  sourceLabelMap,
}: UseElementMeasureTransportOptions) {
  const scopeFromHost = useDashboardScope();
  const scope = scopeOverride ?? scopeFromHost;
  const measureKind = resolveMeasureKindFromElement(element, plotTypeOverride);

  const measureContext = useMemo((): MeasureRequestContext => {
    const base: MeasureRequestContext = {
      scope,
      thresholdRequest,
      decimation: decimationPolicy,
      sourceLabelMap,
    };

    if (measureKind === 'timeseries' || measureKind === 'scatter') {
      return { ...base, plotType: measureKind };
    }

    if (measureKind === 'tablebycampaign') {
      return {
        ...base,
        decimation: decimationPolicy.enabled
          ? decimationPolicy
          : { enabled: true, points: 2000 },
      };
    }

    if (measureKind === 'ombrothermic' || measureKind === 'windrose' || measureKind === 'bar') {
      return { ...base, decimation: { enabled: false } };
    }

    return base;
  }, [decimationPolicy, measureKind, scope, sourceLabelMap, thresholdRequest]);

  const effectiveDecimationPolicy = measureContext.decimation ?? decimationPolicy;

  const elementRequest = useMemo(
    () => buildMeasureRequestFromElement(element, measureContext),
    [element, measureContext]
  );

  const transportUrl = elementRequest?.url ?? MEASURES_V2_URL;

  const mainBody = useMemo(
    () =>
      clonePythonOnlyMeasureBody(
        elementRequest?.body ?? createEmptyMeasureBody()
      ),
    [elementRequest?.body]
  );
  const detailBody = useMemo(
    () =>
      clonePythonOnlyMeasureBody(
        elementRequest?.body ?? createEmptyMeasureBody()
      ),
    [elementRequest?.body]
  );

  const transport = useSessionCacheTransport<MeasureRow>({
    mainUrl: transportUrl,
    mainBody,
    detailUrl: transportUrl,
    detailBody,
  });

  return {
    transport,
    measureRequestSignature: elementRequest?.signature,
    usesElementMeasureRequest: elementRequest !== undefined,
    elementRequest,
    effectiveDecimationPolicy,
  };
}

export { supportsElementMeasureRequest, type ElementMeasureRequest };
